import { baseUrl, apiKey } from '@/config';

export class RankingService {
  /**
   * Fetch standings/rankings by league ID
   * @param {number} leagueId - League ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>}
   */
  static async getRankingsByLeagueId(leagueId, params = {}) {
    try {
      const { season, type = 'total' } = params;

      const queryParams = new URLSearchParams({ leagueId });
      if (season) queryParams.append('season', season);
      if (type) queryParams.append('type', type);

      const url = `${baseUrl}/standing/?${queryParams}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-signature': apiKey || '',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data?.data?.result || null;
    } catch (error) {
      console.error(`Error fetching rankings for league ${leagueId}:`, error);
      throw error;
    }
  }

  /**
   * Parse and organize ranking data by tabs
   * @param {Object} standingsData - Raw standings data from API
   * @returns {Object} - Organized data with available tabs
   */
  static parseRankingData(standingsData) {
    if (!standingsData) {
      return {
        availableTabs: [],
        data: {},
        league: null,
      };
    }

    const availableTabs = [];
    const data = {};

    // group stages (Type 2: C1, C2)
    if (standingsData.roundScoreItems && standingsData.roundScoreItems.length > 0) {
      const round = standingsData.roundScoreItems[0];
      if (round.groupScoreItems && round.groupScoreItems.length > 0) {
        round.groupScoreItems.forEach((group) => {
          const tabKey = group.groupName || `group_${group.id}`;
          availableTabs.push(tabKey);
          data[tabKey] = group.scoreItems || [];
        });
      }
      return {
        availableTabs,
        data,
        league: standingsData.league || null,
      };
    }

    if (standingsData.totalStandings && standingsData.totalStandings.length > 0) {
      availableTabs.push('totalStandings');
      data.totalStandings = standingsData.totalStandings;
    }

    if (standingsData.homeStandings && standingsData.homeStandings.length > 0) {
      availableTabs.push('homeStandings');
      data.homeStandings = standingsData.homeStandings;
    }

    if (standingsData.awayStandings && standingsData.awayStandings.length > 0) {
      availableTabs.push('awayStandings');
      data.awayStandings = standingsData.awayStandings;
    }

    if (standingsData.halfStandings && standingsData.halfStandings.length > 0) {
      availableTabs.push('halfStandings');
      data.halfStandings = standingsData.halfStandings;
    }

    return {
      availableTabs,
      data,
      league: standingsData.league || null,
    };
  }
}
